import { apiGet, API_BASE_URL, getBaseAuthHeaders } from './client';

// ===== Types =====

/**
 * MKS-332 — `totalCosts` and `margin` are OPTIONAL: the API omits them
 * entirely for anyone who is not admin or finance (the key is absent, not
 * null, so `undefined` is the honest type). A surface must therefore ask
 * whether it HAS the figure before rendering it; it cannot assume 0.
 */
export interface ProjectReportRow {
  projectId: number;
  projectName: string;
  totalHours: number;
  totalBilling: number;
  /**
   * MKS-332 follow-up — Invoiced and Paid, the apportioned per-project
   * shares. Optional because they arrive only once the backend carrying them
   * is deployed; a surface renders 0 rather than assuming their presence.
   * Unlike `totalCosts` they are NOT redacted — a manager keeps them.
   */
  totalInvoiced?: number;
  totalPaid?: number;
  totalCosts?: number;
  margin?: number;
  users: {
    userId: number;
    userName: string;
    hours: number;
    billing: number;
    costs?: number;
  }[];
}

export interface ProjectReport {
  rows: ProjectReportRow[];
  totals: {
    totalHours: number;
    totalBilling: number;
    totalInvoiced?: number;
    totalPaid?: number;
    totalCosts?: number;
    margin?: number;
  };
}

/**
 * MKS-316 — which entry states the report counts as logged hours.
 * `all` (the default) adds `submitted` and `open` to `approved`.
 */
export type AttendanceEntryState = 'approved' | 'all';

/** MKS-316 — one column of the grid. Weekends/holidays are period-wide, not per person. */
export interface AttendanceCalendarDay {
  date: string;
  isWeekend: boolean;
  isHoliday: boolean;
  holidayName: string | null;
  isWorkingDay: boolean;
}

/** MKS-316 — one approved time-off type covering (part of) one day. */
export interface AttendanceTimeOffSlice {
  typeId: number | null;
  typeName: string;
  typeCode: string | null;
  colour: string;
  /** In `TimeOffDay` units: 8 is a whole day whatever the person's FTE. */
  hours: number;
}

/** MKS-316 — one person on one day. */
export interface AttendanceDayCell {
  date: string;
  hours: number;
  unapprovedHours: number;
  timeOff: AttendanceTimeOffSlice[];
}

/** MKS-316 — days off in the period, grouped by type, in whole-day units. */
export interface AttendanceDaysOff {
  typeId: number | null;
  typeName: string;
  colour: string;
  days: number;
}

export interface AttendanceRow {
  userId: number;
  userName: string;
  email: string;
  totalHours: number;
  daysWorked: number;
  avgHoursPerDay: number;
  dailyBreakdown: { date: string; hours: number }[];
  employeeTypeName?: string | null;
  unapprovedHours?: number;
  days?: AttendanceDayCell[];
  workingDays?: number;
  /** `null` for somebody with no hours target (a contractor) — never 0. */
  expectedHours?: number | null;
  missingHours?: number | null;
  timeOffHours?: number;
  daysOff?: number;
  daysOffByType?: AttendanceDaysOff[];
}

export interface AttendanceReport {
  rows: AttendanceRow[];
  totals: {
    totalHours: number;
    totalDaysWorked: number;
    unapprovedHours?: number;
    expectedHours?: number;
    missingHours?: number;
    timeOffHours?: number;
    workingDays?: number;
  };
  calendar?: AttendanceCalendarDay[];
  entryState?: AttendanceEntryState;
}

export interface PayrollRow {
  userId: number;
  userName: string;
  email: string;
  totalHours: number;
  totalCosts: number;
  userCosts: number;
  extraCosts: number;
  assignmentCosts: number;
}

export interface PayrollReport {
  rows: PayrollRow[];
  totals: {
    totalHours: number;
    totalCosts: number;
    userCosts: number;
    extraCosts: number;
    assignmentCosts: number;
  };
}

export interface PayrollDailyBreakdown {
  date: string;
  workHours: number;
  vacationHours: number;
  nonPaidHours: number;
}

export interface PayrollDailyRow {
  userId: number;
  userName: string;
  email: string;
  dailyBreakdown: PayrollDailyBreakdown[];
  totalWorkHours: number;
  totalVacationHours: number;
  totalNonPaidHours: number;
}

export interface PayrollDailyReport {
  rows: PayrollDailyRow[];
}

export interface PayrollProjectInvoice {
  id: number;
  invoiceNumber: string | null;
}

export interface PayrollProjectEntry {
  projectName: string;
  hours: number;
  invoicedHours: number;
  billable: boolean;
  invoices: PayrollProjectInvoice[];
}

export interface PayrollProjectsRow {
  userId: number;
  userName: string;
  projects: PayrollProjectEntry[];
}

export interface PayrollProjectsReport {
  rows: PayrollProjectsRow[];
}

// ===== Invoicing Status Report Types =====

export interface InvoicingStatusRow {
  id: number;
  date: string;
  projectId: number;
  projectName: string;
  userId: number;
  userName: string;
  hours: number;
  billingRate: number;
  totalBilling: number;
  invoiceId: number | null;
  invoiceName: string | null;
  task: string | null;
  description: string | null;
}

export interface InvoicingStatusReport {
  rows: InvoicingStatusRow[];
  totals: {
    totalHours: number;
    totalBilling: number;
    invoicedCount: number;
    invoicedHours: number;
    invoicedAmount: number;
    uninvoicedCount: number;
    uninvoicedHours: number;
    uninvoicedAmount: number;
  };
}

// ===== Billability Report Types (Phase 57) =====

export interface BillabilityRow {
  userId: number;
  userName: string;
  email: string;
  /**
   * MKS-331 — the person's employee type name, or null for a user with no category. Backs
   * the report's employee-type filter, whose options are the distinct values across the
   * rows. Optional on the type because a response cached by an older build has no such key.
   */
  employeeTypeName?: string | null;
  /**
   * MKS-479 — the person is archived (soft-deleted) today, but their approved
   * hours fall inside the reported period, so the report lists them. Optional
   * for the same reason as `employeeTypeName`: a response cached by an older
   * build has no such key.
   */
  isArchived?: boolean;
  billableHours: number;
  internalHours: number;
  ptoHours: number;
  otherHours: number;
  totalHours: number;
  billableValue: number;
  invoicedAmount: number;
  /**
   * MKS-332 follow-up — Paid at the person grain: this person's
   * `invoicedAmount` restricted to the invoices whose payment was recorded in
   * the window. Optional only because it arrives with the backend that
   * carries it.
   */
  paidAmount?: number;
  cost: number;
  userCosts: number;
  extraCosts: number;
  assignmentCosts: number;
  margin1: number;
  marginPct1: number | null;
  margin2: number;
  marginPct2: number | null;
  /**
   * G-05 — Per-user engineering output for the period. Sourced from the
   * `engineering_commits` table (Phase 32 — Engineering Metrics) filtered to
   * `bucket='human'` only (excludes `ai_assisted` and `automation` per locked
   * decision). `authorUserId IS NOT NULL` — anonymous/unmapped commits ignored.
   * Idle users (zero `human` commits in the period) get
   * `{ commitCount: 0, additions: 0, deletions: 0 }` — the FE renders this as
   * an em-dash.
   */
  workDelivered: { commitCount: number; additions: number; deletions: number };
  /**
   * Per-user timesheets whose Period overlaps the report's date range. Rendered
   * as one link per timesheet to `/manager/timesheets/:id`. Empty when the user
   * has no active timesheet in the range.
   */
  timesheets: { id: number; periodName: string; state: string | null }[];
}

/**
 * MKS-206 — one invoice in the report range that was issued for less than the
 * work it covers. `BillabilityRow.invoicedAmount` is summed from per-entry
 * GROSS billing, so it overstates real revenue by exactly `discountAmount` on
 * each of these.
 */
export interface BillabilityDiscountedInvoice {
  invoiceId: number;
  invoiceNumber: string | null;
  name: string | null;
  /** NET — what the client was actually invoiced. */
  invoicedAmount: number;
  /** GROSS — what it would have been before the discount. */
  preDiscountGross: number;
  discountAmount: number;
}

export interface BillabilityReport {
  rows: BillabilityRow[];
  /** MKS-206 — newest first; empty on a period with no discounted invoice. */
  discountedInvoices: BillabilityDiscountedInvoice[];
  totals: {
    billableHours: number;
    internalHours: number;
    ptoHours: number;
    otherHours: number;
    totalHours: number;
    billableValue: number;
    invoicedAmount: number;
    /** MKS-332 follow-up — Σ of the per-row `paidAmount`. */
    paidAmount?: number;
    /** MKS-206 — by how much `invoicedAmount` above overstates real revenue. */
    invoicedDiscountAmount: number;
    cost: number;
    userCosts: number;
    extraCosts: number;
    assignmentCosts: number;
    margin1: number;
    marginPct1: number | null;
    margin2: number;
    marginPct2: number | null;
    /** G-05 — sum of `workDelivered` across all per-user rows. */
    workDelivered: { commitCount: number; additions: number; deletions: number };
  };
}

// ===== Report API =====

export interface ReportFilters {
  from: string;
  to: string;
  projectIds?: number[];
  userIds?: number[];
}

function buildReportQuery(filters: ReportFilters, extraIds?: { key: string; ids?: number[] }): string {
  const params = new URLSearchParams();
  params.set('from', filters.from);
  params.set('to', filters.to);
  if (extraIds?.ids?.length) {
    params.set(extraIds.key, extraIds.ids.join(','));
  }
  return params.toString();
}

export async function getProjectReport(filters: ReportFilters): Promise<ProjectReport> {
  const query = buildReportQuery(filters, { key: 'projectIds', ids: filters.projectIds });
  const res = await apiGet<{ data: ProjectReport }>(`/reports/project?${query}`);
  return res.data;
}

/**
 * MKS-316 — the attendance report's own filter shape. `entryState` and
 * `projectIds` reach the server because they change which hours count;
 * employee type, the person search and the missing-hours sort stay on the
 * client over the rows already fetched.
 */
export interface AttendanceReportFilters extends ReportFilters {
  entryState?: AttendanceEntryState;
}

function buildAttendanceQuery(filters: AttendanceReportFilters): string {
  const params = new URLSearchParams();
  params.set('from', filters.from);
  params.set('to', filters.to);
  if (filters.userIds?.length) params.set('userIds', filters.userIds.join(','));
  if (filters.projectIds?.length) params.set('projectIds', filters.projectIds.join(','));
  if (filters.entryState) params.set('entryState', filters.entryState);
  return params.toString();
}

export async function getAttendanceReport(
  filters: AttendanceReportFilters
): Promise<AttendanceReport> {
  const res = await apiGet<{ data: AttendanceReport }>(
    `/reports/attendance?${buildAttendanceQuery(filters)}`
  );
  return res.data;
}

export async function downloadAttendanceReportXlsx(
  filters: AttendanceReportFilters
): Promise<Blob> {
  const response = await fetch(
    `${API_BASE_URL}/reports/attendance/xlsx?${buildAttendanceQuery(filters)}`,
    { method: 'GET', credentials: 'include', headers: getBaseAuthHeaders() }
  );

  if (!response.ok) {
    throw new Error(`Failed to download report: ${response.statusText}`);
  }

  return response.blob();
}

export async function getPayrollReport(filters: ReportFilters): Promise<PayrollReport> {
  const query = buildReportQuery(filters, { key: 'userIds', ids: filters.userIds });
  const res = await apiGet<{ data: PayrollReport }>(`/reports/payroll?${query}`);
  return res.data;
}

export async function getPayrollDailyReport(filters: ReportFilters): Promise<PayrollDailyReport> {
  const query = buildReportQuery(filters, { key: 'userIds', ids: filters.userIds });
  const res = await apiGet<{ data: PayrollDailyReport }>(`/reports/payroll/daily?${query}`);
  return res.data;
}

export async function downloadPayrollDailyReportXlsx(filters: ReportFilters): Promise<Blob> {
  const query = buildReportQuery(filters, { key: 'userIds', ids: filters.userIds });

  const response = await fetch(`${API_BASE_URL}/reports/payroll/daily/xlsx?${query}`, {
    method: 'GET',
    credentials: 'include',
    headers: getBaseAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to download report: ${response.statusText}`);
  }

  return response.blob();
}

export async function getPayrollProjectsReport(filters: ReportFilters): Promise<PayrollProjectsReport> {
  const query = buildReportQuery(filters, { key: 'userIds', ids: filters.userIds });
  const res = await apiGet<{ data: PayrollProjectsReport }>(`/reports/payroll/projects?${query}`);
  return res.data;
}

export async function downloadPayrollProjectsReportXlsx(filters: ReportFilters): Promise<Blob> {
  const query = buildReportQuery(filters, { key: 'userIds', ids: filters.userIds });

  const response = await fetch(`${API_BASE_URL}/reports/payroll/projects/xlsx?${query}`, {
    method: 'GET',
    credentials: 'include',
    headers: getBaseAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to download report: ${response.statusText}`);
  }

  return response.blob();
}

export async function getInvoicingStatusReport(filters: ReportFilters): Promise<InvoicingStatusReport> {
  const query = buildReportQuery(filters);
  const res = await apiGet<{ data: InvoicingStatusReport }>(`/reports/invoicing-status?${query}`);
  return res.data;
}

export async function getBillabilityReport(filters: ReportFilters): Promise<BillabilityReport> {
  const query = buildReportQuery(filters, { key: 'userIds', ids: filters.userIds });
  const res = await apiGet<{ data: BillabilityReport }>(`/reports/billability?${query}`);
  return res.data;
}

// ===== Monthly Revenue KPI (P1 Finance) =====

/** One label slice of a month's revenue. `labelId: null` = the No-label bucket. */
export interface RevenueKpiLabelSlice {
  labelId: number | null;
  revenue: number; // CZK
}

export interface RevenueKpiMonth {
  month: string; // 'YYYY-MM'
  revenue: number; // CZK
  invoiceCount: number;
  /** Per-label split of `revenue`. Slices sum to `revenue`; zeros omitted. */
  byLabel: RevenueKpiLabelSlice[];
  /**
   * This month's CATCH-UP target: the annual target less what the year has
   * already invoiced, spread over the months that remain. Rises after a
   * shortfall, falls after an overshoot, resets each January.
   *
   * `null` = nothing to show — either no annual target is configured, or the
   * month lies beyond the one in progress (its basis has not happened yet).
   */
  target: number | null;

  // ---------------------------------------------------------------------
  // MKS-210. The should-invoice / gap / forecast layer: `null` or empty for
  // a month that has nothing to report.
  // ---------------------------------------------------------------------

  /**
   * What the month SHOULD have billed: `revenue` plus everything the
   * To-Be-Invoiced queue still holds open for it. `null` beyond the month in
   * progress — the queue cannot speak for work not yet done.
   */
  shouldInvoice: number | null;
  /** `shouldInvoice - revenue`, i.e. the still-open queue. */
  gap: number | null;
  /** The projects behind `gap`, largest first. */
  gapProjects: RevenueKpiGapProject[];
  /** Projected recurring retainer — future months only. */
  forecast: number | null;
}

/** One project's share of a month's gap. */
export interface RevenueKpiGapProject {
  projectId: number;
  projectName: string;
  amount: number; // CZK
}

/** How the forecast series was derived. */
export interface RevenueKpiForecast {
  /** Months averaged (1..12; 3 by default). */
  windowMonths: number;
  /** The complete months actually averaged, oldest first. */
  basisMonths: string[];
  /** The figure every future month carries. */
  monthlyAmount: number | null;
}

/**
 * A filterable/stackable project label. `id: null` is the No-label
 * pseudo-label — revenue from projects carrying no label at all.
 *
 * This list is always the FULL option set, never narrowed by the active
 * filter, so filtering can't collapse the filter's own choices.
 *
 * A project carrying several labels is counted once, under its most SPECIFIC
 * label — the one with the lowest `projectCount` — so a blanket flag like
 * `Billable` never swallows a bar. See `primaryLabelOf` in the backend's
 * `revenueKpiService`.
 */
export interface RevenueKpiLabel {
  id: number | null;
  name: string;
  color: string | null;
  /** How many projects carry this label overall (also the specificity key). */
  projectCount: number;
}

export interface RevenueKpiReport {
  /** The calendar year this report covers. */
  year: number;
  /** Years the selector may offer, newest first. Never narrowed by the filter. */
  availableYears: number[];
  /**
   * FLAT per-month target (`annual_revenue_target_czk / 12`). The chart plots
   * each month's own catch-up target (`months[].target`); this baseline is
   * only quoted as a "flat share" hint. 0 = no target.
   */
  target: number;
  /** The admin-configured YEARLY target (`annual_revenue_target_czk`). */
  annualTarget: number;
  currency: string;
  labels: RevenueKpiLabel[];
  months: RevenueKpiMonth[];
  /** MKS-210 — `null` while the should-invoice/forecast flag is off. */
  forecast: RevenueKpiForecast | null;
  /**
   * MKS-210 — a non-CZK paušál in the queue had no reachable ČNB fixing, so
   * the gap omits it. Shown as a caveat rather than silently swallowed.
   */
  gapFxUnavailable: boolean;
}

/** Wire token for the No-label bucket in the `labelIds` CSV param. */
export const NO_LABEL_TOKEN = 'none';

/** Serialize a label selection for the `labelIds` query param. */
export function serializeLabelIds(ids: (number | null)[]): string {
  return ids.map((id) => (id === null ? NO_LABEL_TOKEN : String(id))).join(',');
}

export async function getRevenueKpi(
  year?: number,
  labelIds?: (number | null)[],
  forecastWindow?: number
): Promise<RevenueKpiReport> {
  const params = new URLSearchParams();
  // Omitted year = the backend's current-year default.
  if (year !== undefined) params.set('year', String(year));
  // An empty selection means "all labels" — same as omitting the param.
  if (labelIds && labelIds.length > 0) {
    params.set('labelIds', serializeLabelIds(labelIds));
  }
  // Omitted = the backend's 3-complete-month default.
  if (forecastWindow !== undefined) params.set('forecastWindow', String(forecastWindow));
  const res = await apiGet<{ data: RevenueKpiReport }>(`/reports/revenue-kpi?${params}`);
  return res.data;
}

// ===== Billability User-Trend (quick task 260602-g6r) =====

/**
 * One month entry in the trailing-12-month series returned by
 * GET /api/reports/billability/user-trend?userId=<id>. The backend always
 * returns exactly 12 entries ordered oldest → newest; idle months render
 * margin1=0 and commitCount=0 (never undefined).
 */
export interface BillabilityUserTrendMonth {
  yearMonth: string;
  margin1: number;
  commitCount: number;
}

export interface BillabilityUserTrend {
  userId: number;
  months: BillabilityUserTrendMonth[];
}

export async function getBillabilityUserTrend(
  userId: number
): Promise<BillabilityUserTrend> {
  const res = await apiGet<{ data: BillabilityUserTrend }>(
    `/reports/billability/user-trend?userId=${userId}`
  );
  return res.data;
}

// ===== Detailed Project Report Types =====

export interface DetailedReportFilters {
  projectIds: number[];
  from: string;
  to: string;
  includeSubprojects?: boolean;
  includeExpenses?: boolean;
  includeExtraGains?: boolean;
  includeCosts?: boolean;
  includeNotApproved?: boolean;
  includeRejected?: boolean;
}

export interface ProjectInfo {
  id: number;
  name: string;
  fullName: string;
  parentId: number | null;
  isSubproject: boolean;
}

export interface BillingRow {
  projectId: number;
  projectName: string;
  billingRate: number;
  hours: number;
  amount: number;
}

export interface CostsSummary {
  laborCosts: number;
  expenses: number;
  totalCosts: number;
}

export interface EntryRow {
  id: number;
  date: string;
  projectId: number;
  projectName: string;
  userId: number;
  userName: string;
  hours: number;
  description: string | null;
  task: string | null;
  billingRate: number;
  totalBilling: number;
  totalCosts: number;
  roleName: string | null;
}

export interface ExpenseRow {
  id: number;
  date: string;
  projectId: number;
  projectName: string;
  description: string | null;
  amount: number;
}

export interface ExtraGainRow {
  id: number;
  date: string;
  projectId: number;
  projectName: string;
  description: string | null;
  amount: number;
  invoiceNumber: string | null;
}

export interface TaskSummary {
  task: string;
  hours: number;
  entries: number;
}

export interface InvoiceRow {
  id: number;
  name: string | null;
  invoiceNumber: string | null;
  orderNumber: string | null;
  from: string;
  to: string;
  invoicedAmount: number;
  projectNames: string[];
}

export interface ReportTotals {
  hours: number;
  billing: number;
  // MKS-332 — omitted for anyone who is not admin or finance.
  costs?: number;
  expenses?: number;
  extraGains: number;
  invoiced: number;
  uninvoicedHours: number;
  uninvoicedBilling: number;
  margin?: number;
}

export interface DetailedProjectReport {
  projects: ProjectInfo[];
  period: { from: string; to: string };
  billing: BillingRow[];
  costs: CostsSummary | null;
  entries: EntryRow[];
  expenses: ExpenseRow[];
  extraGains: ExtraGainRow[];
  invoices: InvoiceRow[];
  tasks: TaskSummary[];
  totals: ReportTotals;
}

// ===== Detailed Project Report API =====

function buildDetailedReportQuery(filters: DetailedReportFilters): string {
  const params = new URLSearchParams();
  params.set('from', filters.from);
  params.set('to', filters.to);
  params.set('projectIds', filters.projectIds.join(','));
  if (filters.includeSubprojects) params.set('includeSubprojects', 'true');
  if (filters.includeExpenses) params.set('includeExpenses', 'true');
  if (filters.includeExtraGains) params.set('includeExtraGains', 'true');
  if (filters.includeCosts) params.set('includeCosts', 'true');
  if (filters.includeNotApproved) params.set('includeNotApproved', 'true');
  if (filters.includeRejected) params.set('includeRejected', 'true');
  return params.toString();
}

export async function getDetailedProjectReport(
  filters: DetailedReportFilters
): Promise<DetailedProjectReport> {
  const query = buildDetailedReportQuery(filters);
  const res = await apiGet<{ data: DetailedProjectReport }>(`/reports/project/detailed?${query}`);
  return res.data;
}

export async function downloadDetailedProjectReportXlsx(
  filters: DetailedReportFilters
): Promise<{ blob: Blob; filename: string | null }> {
  const query = buildDetailedReportQuery(filters);

  const response = await fetch(`${API_BASE_URL}/reports/project/detailed/xlsx?${query}`, {
    method: 'GET',
    credentials: 'include',
    headers: getBaseAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to download report: ${response.statusText}`);
  }

  const disposition = response.headers.get('Content-Disposition');
  const match = disposition?.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] ?? null;

  return { blob: await response.blob(), filename };
}
